import { html, render, nothing, svg, type TemplateResult } from 'lit-html';
import panelStyles from './pk-report-breakdown.css?inline';
import sharedTokens from '../pk-report-shared/pk-report-tokens.css?inline';

/**
 * `<pk-report-breakdown>` — proportional-share visualization (pie or treemap). v0.7
 *
 * Accepts `<pk-report-slice>` children with label, value (number), and color.
 * Fires `pk-slice-click` with `{ label, value, percentage }` on interaction.
 *
 * Attributes:
 *   `mode`  — `pie` (default) | `treemap`. treemap auto-switches for >5 categories.
 *   `title` — optional panel heading.
 *
 * Events:
 *   `pk-slice-click` — `detail: { label: string, value: number, percentage: number }`.
 *
 * ```html
 * <pk-report-breakdown mode="pie" title="Tokens by type">
 *   <pk-report-slice label="Input"     value="42100" color="var(--pk-color-info)"></pk-report-slice>
 *   <pk-report-slice label="Output"    value="18750" color="var(--pk-color-warn)"></pk-report-slice>
 *   <pk-report-slice label="Cache hit" value="9200"  color="var(--pk-text-muted)"></pk-report-slice>
 * </pk-report-breakdown>
 * ```
 */
export class PkReportBreakdown extends HTMLElement {
    static readonly tagName = 'pk-report-breakdown';

    static get observedAttributes(): string[] {
        return ['mode', 'title'];
    }

    private readonly _root: ShadowRoot;
    private readonly _mutationObserver: MutationObserver;
    private _segmentsData: PkBreakdownSegment[] = [];

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
        this._mutationObserver = new MutationObserver(() => this._doRender());
    }

    connectedCallback(): void {
        this._upgradeProperty('segments');
        this._mutationObserver.observe(this, { childList: true, subtree: true, attributes: true });
        this._doRender();
    }

    // ── Compat data API ────────────────────────────────────────────────────────

    /**
     * Imperative data setter (v0.8 compat). Builds `<pk-report-slice>` children
     * from a plain array so CMS Razor pages can drive the breakdown via
     * `el.segments = [...]`. Backward-compatible with hand-authored markup.
     */
    set segments(value: PkBreakdownSegment[] | null | undefined) {
        this._segmentsData = Array.isArray(value) ? value : [];
        this._rebuildSlices();
    }

    get segments(): PkBreakdownSegment[] {
        return this._segmentsData;
    }

    private _rebuildSlices(): void {
        this.querySelectorAll('pk-report-slice').forEach(el => el.remove());
        for (const seg of this._segmentsData) {
            const slice = document.createElement('pk-report-slice');
            slice.setAttribute('label', seg.label ?? '');
            slice.setAttribute('value', String(seg.value ?? 0));
            if (seg.color) slice.setAttribute('color', seg.color);
            this.appendChild(slice);
        }
        // MutationObserver fires _doRender on appended children.
    }

    private _upgradeProperty(prop: string): void {
        if (Object.prototype.hasOwnProperty.call(this, prop)) {
            const v: unknown = Reflect.get(this, prop);
            Reflect.deleteProperty(this, prop);
            Reflect.set(this, prop, v);
        }
    }

    disconnectedCallback(): void {
        this._mutationObserver.disconnect();
    }

    attributeChangedCallback(): void {
        this._doRender();
    }

    private _mode(): 'pie' | 'treemap' {
        const v = this.getAttribute('mode');
        return v === 'treemap' ? 'treemap' : 'pie';
    }

    /** Collect slice data from child <pk-report-slice> elements. */
    private _slices(): SliceData[] {
        const sliceEls = Array.from(this.querySelectorAll('pk-report-slice'));
        const raw = sliceEls.map(el => ({
            label: el.getAttribute('label') ?? '',
            value: parseFloat(el.getAttribute('value') ?? '0') || 0,
            color: el.getAttribute('color') ?? 'var(--pk-color-primary, #0e7c7b)',
        }));
        const total = raw.reduce((s, r) => s + r.value, 0);
        if (total === 0) return raw.map(r => ({ ...r, percentage: 0 }));
        return raw.map(r => ({ ...r, percentage: (r.value / total) * 100 }));
    }

    private _handleSliceClick(slice: SliceData): void {
        this.dispatchEvent(new CustomEvent('pk-slice-click', {
            bubbles:  true,
            composed: true,
            detail:   { label: slice.label, value: slice.value, percentage: slice.percentage },
        }));
    }

    // ── Pie rendering ─────────────────────────────────────────────────────────

    /**
     * Renders a clean SVG pie chart using stroke-dasharray/dashoffset technique.
     * Each segment is a circle stroke offset. This avoids complex arc path math.
     */
    private _renderPie(slices: SliceData[]): TemplateResult {
        const cx = 80, cy = 80, r = 58;
        const circumference = 2 * Math.PI * r;

        // Build cumulative offset: we rotate -90deg on the group to start at top.
        let cumulativePct = 0;
        const segments = slices.map(slice => {
            const dashLen    = (slice.percentage / 100) * circumference;
            const dashOffset = circumference - (cumulativePct / 100) * circumference;
            cumulativePct   += slice.percentage;
            return { ...slice, dashLen, dashOffset };
        });

        return html`
            <div class="pie-container">
                <svg class="pie-svg"
                     viewBox="0 0 160 160"
                     role="img"
                     aria-label="Proportional breakdown chart">
                    <g transform="rotate(-90, 80, 80)">
                        ${segments.map(seg => svg`
                            <circle
                                cx=${cx}
                                cy=${cy}
                                r=${r}
                                fill="none"
                                stroke=${seg.color}
                                stroke-width="20"
                                stroke-dasharray="${seg.dashLen} ${circumference - seg.dashLen}"
                                stroke-dashoffset=${seg.dashOffset}
                                @click=${() => this._handleSliceClick(seg)}
                                role="button"
                                aria-label="${seg.label}: ${seg.percentage.toFixed(1)}%">
                            </circle>
                        `)}
                    </g>
                </svg>
                ${this._renderLegend(slices)}
            </div>
        `;
    }

    // ── Treemap rendering ─────────────────────────────────────────────────────

    /**
     * Simple single-row proportional treemap: cells arranged in a grid where
     * each cell's width fraction matches its percentage. Suitable for up to ~8
     * categories; visual becomes crowded beyond that.
     */
    private _renderTreemap(slices: SliceData[]): TemplateResult {
        const gridCols = slices.map(s => `${s.percentage.toFixed(1)}fr`).join(' ');

        return html`
            <div class="treemap-container" style="grid-template-columns: ${gridCols};">
                ${slices.map(s => html`
                    <div class="treemap-cell"
                         style="background: ${s.color}; height: 120px;"
                         role="button"
                         tabindex="0"
                         aria-label="${s.label}: ${s.percentage.toFixed(1)}%"
                         @click=${() => this._handleSliceClick(s)}
                         @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._handleSliceClick(s); } }}>
                        <span class="treemap-label">${s.label}</span>
                    </div>
                `)}
            </div>
            ${this._renderLegend(slices)}
        `;
    }

    // ── Legend ────────────────────────────────────────────────────────────────

    private _renderLegend(slices: SliceData[]): TemplateResult {
        return html`
            <div class="legend">
                ${slices.map(s => html`
                    <div class="legend-item"
                         role="button"
                         tabindex="0"
                         @click=${() => this._handleSliceClick(s)}
                         @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._handleSliceClick(s); } }}>
                        <span class="legend-swatch" style="background: ${s.color}"></span>
                        <span class="legend-label">${s.label}</span>
                        <span class="legend-pct">${s.percentage.toFixed(1)}%</span>
                    </div>
                `)}
            </div>
        `;
    }

    // ── Template ──────────────────────────────────────────────────────────────

    private _template(): TemplateResult {
        const title  = this.getAttribute('title');
        const slices = this._slices();

        // Auto-switch to treemap if >5 slices (regardless of mode attribute)
        const useTreemap = this._mode() === 'treemap' || slices.length > 5;
        const vizContent = slices.length === 0
            ? html`<slot name="empty-state"></slot>`
            : useTreemap
                ? this._renderTreemap(slices)
                : this._renderPie(slices);

        return html`
            <style>${sharedTokens}${panelStyles}</style>
            <div class="breakdown-panel">
                ${title ? html`
                    <div class="breakdown-header">
                        <p class="breakdown-title">${title}</p>
                    </div>
                ` : nothing}
                <div class="breakdown-body">
                    ${vizContent}
                </div>
            </div>
            <!-- Hidden slot for child <pk-report-slice> elements -->
            <slot style="display:none"></slot>
        `;
    }

    private _doRender(): void {
        render(this._template(), this._root);
    }
}

if (!customElements.get(PkReportBreakdown.tagName)) {
    customElements.define(PkReportBreakdown.tagName, PkReportBreakdown);
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * `<pk-report-slice>` — data item for `<pk-report-breakdown>`. v0.7
 *
 * This is a data-only element; it has no visual shadow DOM of its own.
 * `<pk-report-breakdown>` reads its attributes to build the visualization.
 *
 * Attributes:
 *   `label` — dimension label (required).
 *   `value` — numeric value (required).
 *   `color` — CSS color or var() reference (required).
 */
export class PkReportSlice extends HTMLElement {
    static readonly tagName = 'pk-report-slice';

    static get observedAttributes(): string[] {
        return ['label', 'value', 'color'];
    }

    connectedCallback(): void {
        // Notify parent breakdown to re-render
        this.closest('pk-report-breakdown')?.dispatchEvent(
            new CustomEvent('_slice-update', { bubbles: false })
        );
    }

    attributeChangedCallback(): void {
        this.closest('pk-report-breakdown')?.dispatchEvent(
            new CustomEvent('_slice-update', { bubbles: false })
        );
    }
}

if (!customElements.get(PkReportSlice.tagName)) {
    customElements.define(PkReportSlice.tagName, PkReportSlice);
}

// ─────────────────────────────────────────────────────────────────────────────

/** Compat data shape for the `segments` property (CMS-driven imperative API). */
export interface PkBreakdownSegment {
    label?: string;
    value?: number;
    color?: string;
}

interface SliceData {
    label:      string;
    value:      number;
    color:      string;
    percentage: number;
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-report-breakdown': PkReportBreakdown;
        'pk-report-slice':     PkReportSlice;
    }
}
